import React, { useState, useEffect, useCallback, useMemo } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${ASSET_BASE_URL}${url}`;
};

// Deterministic shuffle based on question code so letters don't reshuffle on re-render
const scrambleLetters = (word, seed) => {
  const letters = word.split('');
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  for (let i = letters.length - 1; i > 0; i--) {
    h = ((h << 5) - h + i) | 0;
    const j = Math.abs(h) % (i + 1);
    [letters[i], letters[j]] = [letters[j], letters[i]];
  }
  // If result equals original, swap first two
  if (letters.join('') === word && letters.length > 1) {
    [letters[0], letters[1]] = [letters[1], letters[0]];
  }
  return letters;
};

const QuestionCard = ({ question, questionNumber, isExample, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated, scrambledLetters }) => {
  const imageUrl = question.options?.[0]?.image_url;
  const correctAnswer = question.accepted_answers?.[0] || '';

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectInReview = isReviewMode && !isAnswerCorrect;
  const disabled = isExample || isReviewMode || showHint || !isHydrated;

  return (
    <div className={`p-3 md:p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700'}`}>
      <div className="flex items-center gap-3 md:gap-4">
        {/* Number */}
        <span className="font-bold text-primary-blue text-lg flex-shrink-0 w-6 text-right">
          {isExample ? '' : `${questionNumber}.`}
        </span>

        {/* Image */}
        {imageUrl && (
          <img
            src={resolveUrl(imageUrl)}
            alt="question"
            className="w-16 h-16 md:w-20 md:h-20 object-contain bg-white rounded-xl border border-slate-200 dark:border-slate-600 p-1 flex-shrink-0 shadow-sm"
          />
        )}

        {/* Input */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {isExample ? (
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-2 border-transparent font-bold text-primary-blue/60 italic text-center tracking-[0.3em] text-lg">
              {correctAnswer}
            </div>
          ) : showIncorrectInReview ? (
            <div className="exercise-input-text bg-red-50 px-3 py-1 rounded-lg flex items-center min-w-0 h-11 justify-center">
              {question.user_answer?.answer
                ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
                : <span className="text-gray-400 mr-2 font-bold">__</span>
              }
              <span className="text-green-600 font-bold">{correctAnswer}</span>
            </div>
          ) : (
            <input
              type="text"
              autoComplete="off"
              value={showHint || (isReviewMode && isAnswerCorrect) ? correctAnswer : (answer || '')}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              placeholder={'_ '.repeat(correctAnswer.length).trim()}
              className={`w-full bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 focus:ring-4 focus:ring-blue-100 focus:border-primary-blue rounded-xl px-4 py-2 font-bold text-primary-blue dark:text-blue-400 transition-all outline-none min-w-0 h-11 tracking-[0.2em] text-center ${showHint ? 'hint-input' : ''}`}
              readOnly={disabled}
              disabled={isReviewMode || !isHydrated}
            />
          )}

          {isReviewMode && (
            <div className="shrink-0 flex items-center gap-2">
              {isAnswerCorrect
                ? <span className="correct-checkmark">✓</span>
                : <span className="incorrect-checkmark">✗</span>
              }
              {!isExample && <QuestionLearnButton question={question} />}
            </div>
          )}
        </div>

        {/* Jumbled letters */}
        <div className="flex-shrink-0 flex flex-wrap gap-1 max-w-[5rem] md:max-w-[6rem] justify-center">
          {scrambledLetters.map((letter, idx) => (
            <div
              key={idx}
              className="w-6 h-6 md:w-7 md:h-7 rounded bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 flex items-center justify-center font-bold text-sm md:text-base text-amber-800 dark:text-amber-300 shadow-sm"
            >
              {letter}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StartersReadingPart3 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  // Pre-compute scrambled letters for all questions (stable across re-renders)
  const scrambledMap = useMemo(() => {
    const map = {};
    (exerciseData?.questions || []).forEach(q => {
      const word = q.accepted_answers?.[0] || '';
      map[q.id] = scrambleLetters(word, q.code || q.id);
    });
    return map;
  }, [exerciseData?.questions]);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestions = sortedQuestions.filter(q => q.order_index === 0);
  const mainQuestions = sortedQuestions.filter(q => q.order_index > 0);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col items-center h-full bg-transparent rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
          <div className="flex flex-col gap-3">
            {exampleQuestions.map(q => (
              <QuestionCard
                key={q.id}
                question={q}
                isExample={true}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
                scrambledLetters={scrambledMap[q.id] || []}
              />
            ))}
            {mainQuestions.map((q, index) => (
              <QuestionCard
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
                scrambledLetters={scrambledMap[q.id] || []}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartersReadingPart3;
